import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import {
    CATALOG_IMPORT_TABS,
    isCatalogImportTab,
    type CatalogImportTab,
    type CatalogImportTabCounts,
} from '@/pages/catalogImport/store';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ImportTabsProps = {
    tab: CatalogImportTab;
    counts: CatalogImportTabCounts;
    onTabChange: (tab: CatalogImportTab) => void;
    /** The selected tab's rows: the table. */
    children: React.ReactNode;
};

const TAB_LABEL_KEY = {
    all: 'catalogImport.products.tabs.all',
    needsAttention: 'catalogImport.products.tabs.needsAttention',
    newProducts: 'catalogImport.products.tabs.newProducts',
    restock: 'catalogImport.products.tabs.restock',
    excluded: 'catalogImport.products.tabs.excluded',
} as const satisfies Record<CatalogImportTab, string>;

/**
 * All / Needs attention / New products / Restock / Excluded above the
 * table, each with its count. The selected tab is the caller's (the address holds it), so
 * pressing a tab only reports it.
 *
 * Each tab has its own panel, and only the selected one is mounted, holding
 * `children`: the table over the selected tab's rows. A switch mounts the
 * table afresh, at the top of its rows; the rows themselves are the ones the
 * step already built. (One panel shared by every tab cannot work: React Aria
 * fixes a panel's DOM id when it mounts, so the next tab's `aria-controls`
 * would point at nothing.)
 */
export const ImportTabs: React.FC<ImportTabsProps> = ({
    tab,
    counts,
    onTabChange,
    children,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Tabs
            className="w-full"
            onSelectionChange={(key) => {
                if (isCatalogImportTab(key)) {
                    onTabChange(key);
                }
            }}
            selectedKey={tab}
            variant="secondary"
        >
            <Tabs.ListContainer className="min-w-0 max-w-max">
                <Tabs.List
                    aria-label={t('catalogImport.products.tabs.ariaLabel')}
                >
                    {CATALOG_IMPORT_TABS.map((id) => (
                        // Sized to their text, as the books tabs: a narrow
                        // screen scrolls the list instead of wrapping a label.
                        <Tabs.Tab
                            className="whitespace-nowrap"
                            id={id}
                            key={id}
                            {...getNodeTestIdProp(
                                `catalog-import-products-tab-${id}`,
                            )}
                        >
                            {/* The tab lays its children out as flex items,
                                which drops the space between them on screen:
                                the margin draws the gap, and the space keeps
                                the two words apart in the tab's text. */}
                            {t(TAB_LABEL_KEY[id])}{' '}
                            <span
                                className="ms-1.5 text-muted tabular-nums"
                                {...getNodeTestIdProp(
                                    `catalog-import-products-tab-count-${id}`,
                                )}
                            >
                                {countParam(counts[id])}
                            </span>
                            <Tabs.Indicator />
                        </Tabs.Tab>
                    ))}
                </Tabs.List>
            </Tabs.ListContainer>
            {CATALOG_IMPORT_TABS.map((id) => (
                <Tabs.Panel
                    className="pt-3"
                    id={id}
                    key={id}
                    {...getNodeTestIdProp(
                        `catalog-import-products-tab-panel-${id}`,
                    )}
                >
                    {children}
                </Tabs.Panel>
            ))}
        </Tabs>
    );
};
